import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import type { DtoV1RealEstateOwnedSaveRealEstateOwnedSummaryRequest } from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import lang from '~/common/constants/language.json';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useRealEstateOwnedStore } from './useRealEstateOwnedStore';

const QUERY_KEYS = {
  realEstateOwned: 'realEstateOwned',
};

const getRealEstateOwnedQueryKey = (
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
) => [QUERY_KEYS.realEstateOwned, obfuscatedLoanIdentifier] as const;

export const useGetRealEstateOwnedSummaryQuery = () => {
  const showSnackbar = useSnackbar();
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: getRealEstateOwnedQueryKey(obfuscatedLoanIdentifier),
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.realEstateOwnedGetRealEstateOwnedSummary(
          obfuscatedLoanIdentifier,
        );

      return {
        ...data,
        dropDownOptions: Object.fromEntries(
          Object.entries(data.dropDownOptions).map(([key, value]) => {
            return [
              key,
              value.map((option) => ({
                label: option.value,
                value: option.key,
              })),
            ];
          }),
        ),
      };
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: lang.TOAST_ERROR_STATUS,
        duration: 5000,
      });
    },
  });
};

type DeleteReoMutationPayload = {
  addressId: number;
  addressLdId: number;
};

export const useDeleteRealEstateOwnedMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const closeForm = useRealEstateOwnedStore((state) => state.closeForm);
  const { obfuscatedLoanIdentifier } = useUrl();

  return useMutation({
    mutationKey: ['deleteRealEstateOwned', { obfuscatedLoanIdentifier }],
    mutationFn: async ({
      addressId,
      addressLdId,
    }: DeleteReoMutationPayload) => {
      await typedApiClient.v1.realEstateOwnedDeleteRealEstateOwned({
        obfuscatedLoanIdentifier,
        addressIdentifier: {
          addressId,
          addressLdId,
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getRealEstateOwnedQueryKey(obfuscatedLoanIdentifier),
      });

      closeForm();

      showSnackbar({
        severity: 'success',
        message: 'REO Deleted',
        duration: 5000,
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Record was not able to be deleted.',
        duration: 5000,
      });
    },
  });
};

export const useSaveRealEstateOwnedSummaryMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const closeForm = useRealEstateOwnedStore((state) => state.closeForm);
  const { obfuscatedLoanIdentifier } = useUrl();

  return useMutation({
    mutationKey: ['saveRealEstateOwnedSummary', { obfuscatedLoanIdentifier }],
    mutationFn: async (
      realEstateOwnedSummary: DtoV1RealEstateOwnedSaveRealEstateOwnedSummaryRequest,
    ) => {
      await typedApiClient.v1.realEstateOwnedSaveRealEstateOwnedSummary(
        realEstateOwnedSummary,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getRealEstateOwnedQueryKey(obfuscatedLoanIdentifier),
      });

      closeForm();

      showSnackbar({
        severity: 'success',
        message: 'Saved Successfully',
        duration: 5000,
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Record was not able to be saved.',
        duration: 5000,
      });
    },
  });
};
